import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RowActionMenuItem } from 'src/app/shared/grid/core-grid/rowActionMenuItem.model';
import { ToastService } from 'src/app/services/toast.service';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { TemplateSaveDialogComponent } from 'src/app/common-components/template-save-dialog/template-save-dialog.component';
import { TemplateSaveDialogData, TemplateSaveDialogResult } from 'src/app/common-components/template-save-dialog/template-save-dialog.model';
import { TemplateShareControlDialogComponent } from 'src/app/common-components/template-share-control/template-share-control-dialog.component';
import { TemplateNameCellRow } from 'src/app/shared/helpers/template-name-cell.helper';

export interface TemplateListRow extends TemplateNameCellRow {
  id: string;
  canManage: boolean;
  isLocked: boolean;
}

export interface SharableTemplate {
  isShared: boolean;
  organizationLevelId?: string | null;
  settings?: { organizationLevelId?: string | null } | null;
}

export interface TemplateActionsService<TTemplate extends SharableTemplate> {
  getById(id: string): Observable<TTemplate>;
  rename(template: TTemplate, newName: string): Observable<unknown>;
  duplicate(id: string, name: string): Observable<unknown>;
  setShared(template: TTemplate, isShared: boolean, organizationLevelId?: string | null): Observable<unknown>;
  delete(id: string): Observable<unknown>;
}

export interface TemplateRowActionsConfig<TRow extends TemplateListRow, TTemplate extends SharableTemplate> {
  service: TemplateActionsService<TTemplate>;
  dialog: MatDialog;
  toastService: ToastService;
  translate: (key: string) => string;
  destroy$: Subject<void>;
  reload: () => void;
  deleteTitle: string;
  edit: (row: TRow) => void;
  setDefault: (row: TRow) => Observable<unknown>;
  clearDefault: (row: TRow) => Observable<unknown>;
}

export class TemplateRowActions<TRow extends TemplateListRow, TTemplate extends SharableTemplate> {
  constructor(private readonly config: TemplateRowActionsConfig<TRow, TTemplate>) {}

  buildMenuItems(row: TRow): RowActionMenuItem<TRow>[] {
    return [
      { label: 'Rename', icon: 'edit', disabled: row.isLocked, action: () => this.openRenameDialog(row) },
      { label: 'Edit', icon: 'edit_note', disabled: !row.canManage, action: () => this.config.edit(row) },
      { label: 'Duplicate', icon: 'content_copy', action: () => this.duplicate(row) },
      row.isDefault
        ? { label: 'Remove Default', icon: 'star_border', disabled: !row.canManage, action: () => this.clearDefault(row) }
        : { label: 'Make Default', icon: 'star', disabled: !row.canManage, action: () => this.makeDefault(row) },
      { label: 'Share', icon: 'group_add', disabled: row.isLocked, action: () => this.openShareDialog(row) },
      { label: 'Delete', icon: 'delete', disabled: row.isLocked, action: () => this.openDeleteDialog(row) }
    ];
  }

  openDeleteDialog(row: TRow): void {
    const dialogRef = this.config.dialog.open(DeleteDialog, {
      data: {
        title: this.config.translate(this.config.deleteTitle),
        service: null,
        customDelete: () => this.config.service.delete(row.id)
      },
      width: '440px',
      disableClose: true
    });
    dialogRef.afterClosed().pipe(takeUntil(this.config.destroy$)).subscribe({
      next: (confirmed: boolean) => {
        if (confirmed) this.config.reload();
      }
    });
  }

  private openRenameDialog(row: TRow): void {
    this.openNameDialog({ defaultName: row.name, showShareControl: false }).subscribe({
      next: (result) => {
        if (result && result.name !== row.name) {
          this.rename(row, result.name);
        }
      }
    });
  }

  private duplicate(row: TRow): void {
    this.openNameDialog({
      defaultName: `${row.name} - Copy`,
      title: 'Duplicate Template',
      confirmText: 'Duplicate',
      showShareControl: false
    }).subscribe({
      next: (result) => {
        if (!result) return;
        this.run(
          () => firstValueFrom(this.config.service.duplicate(row.id, result.name)),
          'Template duplicated successfully',
          'Failed to duplicate template'
        );
      }
    });
  }

  private openNameDialog(data: TemplateSaveDialogData): Observable<TemplateSaveDialogResult | null> {
    return this.config.dialog
      .open(TemplateSaveDialogComponent, { data, width: '440px', disableClose: true })
      .afterClosed()
      .pipe(takeUntil(this.config.destroy$));
  }

  private rename(row: TRow, newName: string): void {
    this.run(
      async () => {
        const template = await firstValueFrom(this.config.service.getById(row.id));
        await firstValueFrom(this.config.service.rename(template, newName));
      },
      'Template renamed successfully',
      'Failed to rename template'
    );
  }

  private makeDefault(row: TRow): void {
    this.run(
      () => firstValueFrom(this.config.setDefault(row)),
      'Default template set successfully',
      'Failed to set default template'
    );
  }

  private clearDefault(row: TRow): void {
    this.run(
      () => firstValueFrom(this.config.clearDefault(row)),
      'Default template cleared',
      'Failed to clear default template'
    );
  }

  private async openShareDialog(row: TRow): Promise<void> {
    try {
      const template = await firstValueFrom(this.config.service.getById(row.id));
      const dialogRef = this.config.dialog.open(TemplateShareControlDialogComponent, {
        data: {
          isShared: template.isShared,
          organizationLevelId: template.organizationLevelId,
          dataOrganizationLevelId: template.settings?.organizationLevelId ?? null
        },
        width: '440px'
      });
      dialogRef.afterClosed().pipe(takeUntil(this.config.destroy$)).subscribe({
        next: (result: { isShared: boolean; organizationLevelId: string | null } | null) => {
          if (!result) return;
          this.run(
            () => firstValueFrom(this.config.service.setShared(template, result.isShared, result.organizationLevelId)),
            'Template visibility updated',
            'Failed to update template sharing'
          );
        }
      });
    } catch (error) {
      this.config.toastService.showError(error, this.config.translate('Failed to load template'));
    }
  }

  private async run<T>(action: () => Promise<T>, successKey: string, failureKey: string): Promise<void> {
    try {
      await action();
      this.config.toastService.showSuccess(this.config.translate(successKey));
      this.config.reload();
    } catch (error) {
      this.config.toastService.showError(error, this.config.translate(failureKey));
    }
  }
}
